import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

export default function StatPatientsDialog({ list, onClose }) {
  const [rows, setRows] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    setRows(null);
    api.get("/dashboard/stat-patients", { params: { kind: list.kind } })
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]));
  }, [list.kind]);

  const dateLabel = list.kind === "discharges_today" ? "Discharged" : "Admitted";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="stat-patients-dialog">
        <DialogHeader>
          <DialogTitle data-testid="stat-patients-title">{list.label}</DialogTitle>
          <DialogDescription>{rows ? `${rows.length} patient${rows.length === 1 ? "" : "s"}` : "Loading…"} · click a patient to open the file</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-auto -mx-2">
          {!rows && <div className="py-10 grid place-items-center text-muted-foreground"><Loader2 className="animate-spin" size={18} /></div>}
          {rows && rows.length === 0 && <div className="py-10 text-center text-sm text-muted-foreground" data-testid="stat-patients-empty">No patients in this list.</div>}
          {rows && rows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="text-left px-3 py-2">Patient</th><th className="text-left px-3 py-2">Bed</th><th className="text-left px-3 py-2 hidden sm:table-cell">Diagnosis</th><th className="text-left px-3 py-2">{dateLabel}</th></tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-t border-border hover:bg-muted/40 cursor-pointer" onClick={() => nav(`/patients/${p.id}`)} data-testid={`stat-patient-row-${p.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.hospital_id}{p.critical ? " · " : ""}{p.critical && <Badge variant="destructive" className="ml-1 h-4 px-1.5 text-[10px]">Critical</Badge>}</div>
                    </td>
                    <td className="px-3 py-2">{p.bed_number || "—"}</td>
                    <td className="px-3 py-2 hidden sm:table-cell text-muted-foreground">{p.diagnosis || "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{fmt(list.kind === "discharges_today" ? p.discharge_date : p.admission_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
